// Ionic & Angular Imports
import {Component} from '@angular/core';
import {Events, NavParams, LoadingController, Loading, ModalController, AlertController, Refresher} from 'ionic-angular';

// Service Imports 
import {rsReportSvc, ReportState } from '../../../../services/report.service';
import {rsEquipmentSvc, Equipment} from '../../../../services/equipment.service';
import {rsEquipmentTypeSvc, EquipmentType} from '../../../../services/equipmenttype.service';

// Component Imports
import {BasePage} from '../../../base.component';
import {EquipmentModal} from './equipment-modal/equipment-modal.component';

@Component({
    templateUrl: 'equipment.html',
    providers: [
        rsEquipmentTypeSvc,
        rsEquipmentSvc,
    ]
})
export class EquipmentPage extends BasePage
{
    // Class variables 
    private contentList : Equipment[];
    private equiptypeList : EquipmentType[];

    // Class Constructor
    constructor( 
        protected alertCtrl : AlertController, 
        private loadingCtrl : LoadingController,  
        private navParams : NavParams, 
        private eventSystem : Events,
        private modalCtrl : ModalController,
        private reportSvc: rsReportSvc, 
        private equipmentTypeSvc : rsEquipmentTypeSvc,
        private equipmentSvc : rsEquipmentSvc,
    ){
        // Construct base class
        super(alertCtrl);

        interface NavData {
            equiptype : EquipmentType[];
            equipment : Equipment[];
        };

        // Retrieve the activity list from our NavParams       
        let navData : NavData = <NavData>this.navParams.get('content');

        this.contentList = navData.equipment;
        this.equiptypeList = navData.equiptype;
        this.sortContentList(); this.sortEquipTypesList();

        // Register our event handlers 
        this.registerEventHandlers( eventSystem );
    }

    // Class method used to subscribe to all events this class is interested in
    private registerEventHandlers( eventSystem : Events ) {
        eventSystem.subscribe( 'equipment:deleted', (data) => { this.deleteEquipment( data ); });
        eventSystem.subscribe( 'equipment:updated', (data) => { this.updateEquipment( data ); });
        eventSystem.subscribe( 'equipment:added',   (data) => { this.addEquipment( data ); });
    }
        
    // Sorting method for ensuring our ContentList is always sorted by id (ie., oldest to newest entries)
    private sortContentList(reverse : boolean = false) {
        this.contentList.sort( (a,b) => {
            return reverse ? b.id - a.id : a.id - b.id;
        });
    }

    private sortEquipTypesList(reverse : boolean = false ) {
        this.equiptypeList.sort( (a, b) => {
            let nameA : string = (a.name).toLowerCase();
            let nameB : string = (b.name).toLowerCase();
            if( nameA < nameB ) return -1;
            if( nameA > nameB ) return 1;  
            return 0;
        });
    }

    public getEquipmentThumbnail( item : Equipment ) : string {
        return "/assets/images/missing-thumbnail.png";
    }    

    // Class method used to add a new activity to our ContentList 
    private addEquipment( equipment : Equipment ) : boolean {
        // Add this activity to the end of our ContentList  
        let length : number = this.contentList.push(equipment);

        // Finally, publish an event that our report has been updated
        this.eventSystem.publish( 'report:updated', ReportState.Equipment, length );      
        
        // Return successful
        return true;  
    }

    // Class method used to update an activity in our ContentList
    private updateEquipment( equipment : Equipment ) : boolean {
        // Loop through each item in the ContentList to attempt to find a matching activity
        for( var idx = 0; idx < this.contentList.length; idx++ ) {
            let item : Equipment = this.contentList[idx];
            if( item.id === equipment.id ) {
                // Update the content of this activity within the ContentList
                this.contentList[idx] = equipment;
                return true;          
            }
        }
        // No match was found, return false 
        return false;
    }

    // Class method used to delete an activity in our ContentList
    private deleteEquipment( equipment : Equipment ) : boolean {
        // Loop through each item in the ContentList to attempt to find a matching activity
        for( var idx = 0; idx < this.contentList.length; idx++ ) {
            let item : Equipment = this.contentList[idx];
            if( item.id === equipment.id ) {
                // Remove this activity from the ContentList
                this.contentList.splice( idx, 1 );

                // Publish an event that will update our report state 
                this.eventSystem.publish( 'report:updated', ReportState.Equipment, this.contentList.length );

                // Return successful
                return true;          
            }
        }
        // No match was found, return false 
        return false;
    }

    // Class method used by the template to edit an existing activity
    public updateContent( equipment : Equipment ) {
        // Create our ModalInstance and configure for editing
        this.modalCtrl.create( EquipmentModal, { 
            equiptype: this.equiptypeList,
            equipment: equipment,
            editmode: true
        }).present();
    }

    // Class method used by the template to create a new activity
    public createContent( ) {
          // Create our ModalInstance and configure for creating 
        this.modalCtrl.create( EquipmentModal, { 
            equiptype: this.equiptypeList,
            equipment: null,
            editmode: false
        }).present();      
    }

    // Class method used by the template to delete an activity
    public deleteContent( equipment : Equipment ) {
        // Verify that we have a valid activity id before trying to delete 
        if( equipment.id > 0 ) 
        {
            // Activity id is valid; instantiate delete confirmation
            this.displayConfirmation( 'Delete Equipment', 'Are you sure you want to delete "' + equipment.equipment.name + '" from this report?', () => 
            {
                // User confirmed- so let's initiate the delete process
                let loader : Loading = this.loadingCtrl.create( {
                    content: 'Deleting...'
                });
                loader.present().then( () => {
                    // Data hNow let's start our delete process 
                    this.equipmentSvc.deleteEquipment( equipment.id, this.reportSvc.getActiveReportId() ).subscribe( result => {
                        // Data has been received from our service 
                        this.eventSystem.publish('equipment:deleted', equipment );
                    }, err => {
                        // Operation failed; dismiss loader and shown an error message 
                        loader.dismiss().then( () => {
                            this.displayError("There was an error deleting this equipment.  Please try again later.");
                        });
                    }, () => {
                        // Operation succeeded, dismiss loader
                        loader.dismiss();
                    });
                });
            });
        } else {
            // The activity id supplied is not valid; display a generic error 
            this.displayError( 'An error has occurred.  Please try again later.' );
        }
    }

    // Class method used by the template to refresh the ContentList
    public refreshContent( refresher : Refresher ) {
        // Spawn a spinner while the ContentList is being refreshed
        let loader : Loading = this.loadingCtrl.create( {
            content: 'Refreshing...'
        });
        loader.present().then( () => {
            // The spinner is loaded, so let's get our refreshed ContentList
            this.equipmentSvc.getEquipment( this.reportSvc.getActiveReportId() )
            .subscribe( result => {
                // Data was received from our service 
                this.contentList = result;
                this.sortContentList();

                // Publish an event that will update our report state 
                this.eventSystem.publish( 'report:updated', ReportState.Equipment, this.contentList.length );
            }, err => {
                // The operation failed, so let's handle this error 
                loader.dismiss().then( () => {
                    this.displayError( "There was an error refreshing the list.  Please try again later." ).then( () => {
                        refresher.complete();
                    });
                });
            }, () => {
                // The operation succeeded, let's dismiss our spinner and complete our refresh operation 
                loader.dismiss().then( () => {
                    refresher.complete();
                });
            });
        });
    }
}



// WEBPACK FOOTER //
// ./src/pages/report/tab-main/equipment/equipment.component.ts